// react
import { ReactNode, useContext, useEffect, useState } from 'react';

// azure
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// context
import { ContextBanner, ContextUser } from '../../../App.context';

// hooks
import { useAuth } from '../../../hooks/useAuth';
import { useIdle } from '../../../hooks/useIdle';

// mui
import { Box } from '@mui/material';

// ui
import { Banner } from '../../ui/Banner/Banner';
import { Header } from '../../ui/Header/Header';
import { Nav } from '../../ui/Nav/Nav';
import { Tours } from '../../ui/Tours/Tours';

// dialogs
import { DialogIdleWarning } from '../../dialogs/DialogIdleWarning/DialogIdleWarning';

// utils
import { getToken, isTokenExpired } from '../../../utils/auth';

export interface AuthenticatedProps {
	children: ReactNode;
}

export const Authenticated = ({ children }: AuthenticatedProps) => {
	// hooks
	const { logout } = useAuth();
	const isAuthenticated = useIsAuthenticated();
	const { accounts, inProgress, instance } = useMsal();
	const navigate = useNavigate();

	// context
	const { banner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);

	// logout after being idle for 30 mins
	useIdle({
		onIdle: () => {
			logout();
		},
		onPrompt: () => {
			searchParams.set('dialog', 'idleWarning');
			setSearchParams(searchParams);
		},
		promptTimeout: 1200000,
		timeout: 1800000,
	});

	// state
	const [isNavOpen, setIsNavOpen] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramIsHeadless = Boolean(searchParams.get('isHeadless'));

	// vars
	const isLoggingOut = inProgress === InteractionStatus.Logout;

	const handleOpenNav = () => {
		setIsNavOpen(true);
	};

	const handleCloseNav = () => {
		setIsNavOpen(false);
	};

	// check if token is expired every 3 seconds
	useEffect(() => {
		const intervalCheckToken = setInterval(async () => {
			const token = sessionStorage.getItem('accessToken') as string;
			const isExpired = await isTokenExpired({ token });

			if (isExpired) {
				console.log('Token is expired. Refreshing token...');
				await getToken({ account: accounts[0], instance });
			}
		}, 3000);

		// clear interval when component unmounts
		return () => clearInterval(intervalCheckToken);
	}, [accounts, instance]);

	// navigate to login if not authenticated
	useEffect(() => {
		if (!isLoggingOut && (!isAuthenticated || !user)) {
			navigate(`/${window.location.search}`);
		}
	}, [isAuthenticated, isLoggingOut, navigate, user]);

	if (paramIsHeadless) {
		// If the isHeadless param is set, render only the children without header or nav
		return (
			<Box
				component="main"
				sx={(theme) => {
					return {
						backgroundColor: theme.palette.brand.grey[200],
						minHeight: '100vh',
						padding: theme.spacing(3, 0),
					};
				}}>
				{children}
			</Box>
		);
	}
	return (
		<>
			<Box
				sx={(theme) => {
					return {
						backgroundColor: theme.palette.brand.grey[200],
						minHeight: '100vh',
					};
				}}>
				<Header onOpenNav={handleOpenNav} />
				<Box
					component="main"
					sx={(theme) => {
						return {
							paddingBottom: theme.spacing(3),
							paddingLeft: {
								lg: theme.variables?.navWidthCollapsed,
							},
						};
					}}>
					{banner && <Banner {...banner} />}
					{children}
				</Box>
				<Nav isOpen={isNavOpen} onClose={handleCloseNav} />
			</Box>
			<DialogIdleWarning
				isOpen={paramDialog === 'idleWarning'}
				onClose={(e, reason) => {
					if (reason !== 'backdropClick') {
						searchParams.delete('dialog');
						setSearchParams(searchParams);
					}
				}}
			/>
			<Tours />
		</>
	);
};
